import React from 'react'
import { useApp } from '../../context/AppContext'
import { Service, api } from '../../api/client'
import Panel, { PanelStatus } from './Panel'
import ServiceSheet from './ServiceSheet'
import { lengthSummary, moveId } from './serviceForm'
import { saveErrorMessage, showToast } from './toast'
import { IconButton } from '../../ui/Icon'

export default function ServicesPanel() {
  const { dispatch } = useApp()
  const [status, setStatus] = React.useState<'loading' | 'error' | 'ready'>('loading')
  const [services, setServices] = React.useState<Service[]>([])
  const [editing, setEditing] = React.useState<Service | 'new' | null>(null)
  const [reorderError, setReorderError] = React.useState('')
  const [reordering, setReordering] = React.useState(false)

  const load = React.useCallback(async () => {
    setStatus('loading')
    try {
      setServices(await api.listServices())
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  // refresh fetches the list again while keeping the current one on screen.
  const refresh = React.useCallback(async () => {
    try {
      setServices(await api.listServices())
    } catch {
      // Keep showing the list we have; the next open loads it again.
    }
  }, [])

  const move = async (index: number, delta: -1 | 1) => {
    if (reordering) {
      return
    }
    const ids = moveId(services.map((s) => s.id), index, delta)
    setServices(ids.map((id) => services.find((s) => s.id === id)!))
    setReorderError('')
    setReordering(true)
    try {
      await api.reorderServices(ids)
    } catch (error) {
      setReorderError(saveErrorMessage(error))
      // Show the order the server actually has rather than guessing what to undo.
      await load()
    } finally {
      setReordering(false)
    }
  }

  const saved = (message: string) => {
    setEditing(null)
    refresh()
    showToast(dispatch, message)
  }

  return (
    <>
      <Panel title="Treatments" intro="Your menu, in the order clients see it.">
        {status !== 'ready' ? (
          <PanelStatus status={status} onRetry={load} />
        ) : (
          <>
            {services.length === 0 ? (
              <p className="settings-status">No treatments yet. Add your first one below.</p>
            ) : (
              <ul className="service-list">
                {services.map((service, index) => (
                  <li key={service.id} className="service-item">
                    <span className="category-dot" style={{ background: service.colour }} aria-hidden="true" />
                    <button type="button" className="service-item__main" onClick={() => setEditing(service)}>
                      <span className="service-item__name">{service.name}</span>
                      <span className="service-item__meta">
                        {lengthSummary(service)}
                        {service.bookable_online ? '' : ' · not bookable online'}
                      </span>
                    </button>
                    <span className="move-buttons">
                      <IconButton
                        icon="arrow-up"
                        size={18}
                        className="move-button"
                        label={`Move ${service.name} up`}
                        disabled={reordering || index === 0}
                        onClick={() => move(index, -1)}
                      />
                      <IconButton
                        icon="arrow-down"
                        size={18}
                        className="move-button"
                        label={`Move ${service.name} down`}
                        disabled={reordering || index === services.length - 1}
                        onClick={() => move(index, 1)}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {reorderError && <p className="field-error" role="alert">{reorderError}</p>}
            <button type="button" className="btn btn-primary btn-block" onClick={() => setEditing('new')}>
              Add treatment
            </button>
          </>
        )}
      </Panel>
      {editing && (
        <ServiceSheet
          service={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={saved}
        />
      )}
    </>
  )
}
